import { forwardRef } from "react";

import { Field as BaseUIField } from "@base-ui/react/field";

import { cn } from "../index";

/* -------------------------------------------------------------------------------------------------
 * Root
 * -----------------------------------------------------------------------------------------------*/

type RootProps = BaseUIField.Root.Props;

const Root = forwardRef<HTMLDivElement, RootProps>((props, forwardedRef) => (
  <BaseUIField.Root
    {...props}
    ref={forwardedRef}
    className={cn("gap-1 flex w-full flex-col items-start", props.className)}
  />
));

Root.displayName = "Field.Root";

/* -------------------------------------------------------------------------------------------------
 * Label
 * -----------------------------------------------------------------------------------------------*/

type LabelProps = BaseUIField.Label.Props;

const Label = forwardRef<HTMLDivElement, LabelProps>((props, forwardedRef) => {
  if (props.children == null || props.children === "") return null;

  return (
    <BaseUIField.Label
      {...props}
      ref={forwardedRef}
      className={cn("font-medium text-base text-primary-dark-blue", props.className)}
    />
  );
});

Label.displayName = "Field.Label";

/* -------------------------------------------------------------------------------------------------
 * Control
 * -----------------------------------------------------------------------------------------------*/

type ControlProps = BaseUIField.Control.Props;

const Control = BaseUIField.Control;

Control.displayName = "Field.Control";

/* -------------------------------------------------------------------------------------------------
 * Description
 * -----------------------------------------------------------------------------------------------*/

type DescriptionProps = BaseUIField.Description.Props;

const Description = forwardRef<HTMLDivElement, DescriptionProps>((props, forwardedRef) => {
  if (props.children == null || props.children === "") return null;

  return (
    <BaseUIField.Description
      {...props}
      ref={forwardedRef}
      className={cn("m-0 text-base text-primary-dark-gray", props.className)}
    />
  );
});

Description.displayName = "Field.Description";

/* -------------------------------------------------------------------------------------------------
 * Error
 * -----------------------------------------------------------------------------------------------*/

type ErrorProps = BaseUIField.Error.Props;

const Error = forwardRef<HTMLDivElement, ErrorProps>((props, forwardedRef) => (
  <BaseUIField.Error {...props} ref={forwardedRef} className={cn("text-base text-primary-red", props.className)} />
));

Error.displayName = "Field.Error";

/* -------------------------------------------------------------------------------------------------
 * Errors
 * -----------------------------------------------------------------------------------------------*/

interface ErrorsProps extends Omit<ErrorProps, "children" | "match"> {
  errors?: string[];
}

const Errors = ({ errors, ...props }: ErrorsProps) =>
  errors?.map(message => (
    <Error {...props} key={message} match>
      {message}
    </Error>
  ));

Errors.displayName = "Field.Errors";

export { Control, Description, Error, Errors, Label, Root };
export type { ControlProps, DescriptionProps, ErrorProps, ErrorsProps, LabelProps, RootProps };
